// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, increment, serverTimestamp } from 'firebase/firestore';
import { auth, googleProvider, db, waitForAuthState, isUserAuthenticated } from '../firebase/config';
import {
  getAnonymousSession,
  updateAnonymousSession,
  clearAnonymousSession,
  generateFingerprint,
  linkToRegisteredUser
} from '../utils/anonymousSession';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userCredits, setUserCredits] = useState(0);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);

  // Anonymous user state
  const [anonymousUser, setAnonymousUser] = useState(null);
  const [anonymousCredits, setAnonymousCredits] = useState(0);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Load anonymous session from localStorage
  function loadAnonymousSession() {
    try {
      const session = getAnonymousSession();
      if (session && session.isAnonymous) {
        console.log('👤 Loading anonymous session:', session);

        // Migration: Upgrade old sessions with < 2 credits to 2 credits
        // This ensures users with old sessions (1 credit) get upgraded to the new default (2 credits)
        let credits = session.credits || 0;
        if (credits < 2 && credits >= 0) {
          console.log('⬆️ Upgrading anonymous session from', credits, 'to 2 credits');
          credits = 2;
          // Update the session in localStorage with new credit amount
          updateAnonymousSession({ credits: 2 });
        }

        setAnonymousUser(session);
        setAnonymousCredits(credits);
        setIsAnonymous(true);
      } else {
        setAnonymousUser(null);
        setAnonymousCredits(0);
        setIsAnonymous(false);
      }
    } catch (error) {
      console.error('❌ Error loading anonymous session:', error);
      setAnonymousUser(null);
      setAnonymousCredits(0);
      setIsAnonymous(false);
    }
  }

  // Update anonymous credits
  function updateAnonymousCredits(newCredits) {
    try {
      setAnonymousCredits(newCredits);
      updateAnonymousSession({ credits: newCredits });
      console.log('💰 Anonymous credits updated:', newCredits);
    } catch (error) {
      console.error('❌ Error updating anonymous credits:', error);
    }
  }

  // Clear anonymous session (called after registration)
  async function clearAnonymousSessionData() {
    try {
      // Link anonymous session to registered user if available
      const session = getAnonymousSession();
      if (session && session.sessionId && currentUser) {
        await linkToRegisteredUser(session.sessionId, currentUser.uid);
      }

      // Clear localStorage
      clearAnonymousSession();
      setAnonymousUser(null);
      setAnonymousCredits(0);
      setIsAnonymous(false);
      console.log('🧹 Anonymous session cleared after registration');
    } catch (error) {
      console.error('❌ Error clearing anonymous session:', error);
    }
  }

  // Register new user
  async function signup(email, password, promoCode = null) {
    const result = await createUserWithEmailAndPassword(auth, email, password);

    // Clear anonymous session after registration
    await clearAnonymousSessionData();

    // Note: User document with 10 credits is automatically created by Firebase Auth trigger
    // promoCode will be applied separately in Register.js after signup
    // No need to manually create user document here

    return result;
  }

  // Login existing user
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Google sign-in (popup with redirect fallback)
  async function signInWithGoogle() {
    // Clear anonymous session before sign-in
    await clearAnonymousSessionData();

    try {
      // Try popup first - more reliable for SPAs
      const result = await signInWithPopup(auth, googleProvider);
      return result;
    } catch (error) {
      // If popup fails due to blocker or COOP, fall back to redirect
      if (error.code === 'auth/popup-closed-by-user' ||
          error.code === 'auth/popup-blocked' ||
          error.code === 'auth/cancelled-popup-request') {
        console.log('Popup blocked, falling back to redirect...');
        await signInWithRedirect(auth, googleProvider);
        return null; // Redirect in progress
      }
      throw error;
    }
  }

  // Logout
  function logout() {
    return signOut(auth);
  }

  // Reset password
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  // Create user document in Firestore
  async function createUserDocument(user, additionalData = {}) {
    if (!user) return;
    
    const userRef = doc(db, 'users', user.uid);
    const userData = {
      email: user.email,
      displayName: user.displayName || '',
      credits: additionalData.credits || 0,
      totalProcessed: 0,
      userType: 'registered',
      authProvider: user.providerData?.[0]?.providerId || 'email',
      createdAt: serverTimestamp(),
      ...additionalData
    };
    
    await setDoc(userRef, userData, { merge: true });
  }

  // Helper function to detect if user is newly registered
  function isNewUser(user) {
    if (!user?.metadata?.creationTime || !user?.metadata?.lastSignInTime) {
      return false;
    }

    const createdAt = new Date(user.metadata.creationTime);
    const lastSignIn = new Date(user.metadata.lastSignInTime);
    const now = new Date();
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds

    // BOTH creation and last sign-in must be within 5 minutes
    const isRecentlyCreated = (now - createdAt) <= fiveMinutes;
    const isRecentSignIn = (now - lastSignIn) <= fiveMinutes;

    return isRecentlyCreated && isRecentSignIn;
  }

  // Get user credits with optimistic default for new users
  async function getUserCredits(userId, user = null) {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));
      if (userDoc.exists()) {
        const credits = userDoc.data().credits || 0;
        setUserCredits(credits);
        console.log(`✅ Credits loaded from Firestore: ${credits}`);
        return credits;
      } else {
        // Document doesn't exist - check if user is new
        if (user && isNewUser(user)) {
          console.log(`🆕 New user detected, optimistically showing 10 credits`);
          setUserCredits(10);
          return 10;
        } else {
          console.log(`⚠️ User document not found, defaulting to 0 credits`);
          setUserCredits(0);
          return 0;
        }
      }
    } catch (error) {
      console.error(`❌ Error fetching user credits:`, error);

      // On error, still provide optimistic default for new users
      if (user && isNewUser(user)) {
        console.log(`🆕 Network error but new user detected, optimistically showing 10 credits`);
        setUserCredits(10);
        return 10;
      }

      setUserCredits(0);
      return 0;
    }
  }

  // Update user credits (deduct or add credits)
  async function updateUserCredits(userId, creditChange) {
    try {
      console.log(`💰 AUTH DEBUG: Updating credits for user ${userId} by ${creditChange}`);
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        credits: increment(creditChange),
        lastUpdated: serverTimestamp()
      });

      // Refresh credits in state
      await getUserCredits(userId);
      console.log(`✅ AUTH DEBUG: Credits updated successfully`);
      return true;
    } catch (error) {
      console.error('❌ AUTH DEBUG: Failed to update user credits:', error);
      return false;
    }
  }

  // Update user profile (language, dateFormat, etc.)
  async function updateUserProfile(updates) {
    try {
      if (!currentUser) {
        console.warn('⚠️ Cannot update profile: No user logged in');
        return false;
      }

      console.log(`📝 Updating user profile for ${currentUser.uid}:`, updates);
      const userRef = doc(db, 'users', currentUser.uid);

      await updateDoc(userRef, {
        ...updates,
        lastUpdated: serverTimestamp()
      });

      console.log(`✅ User profile updated successfully`);
      return true;
    } catch (error) {
      console.error('❌ Failed to update user profile:', error);
      return false;
    }
  }

  // Validate current authentication state
  async function validateAuthState() {
    try {
      await waitForAuthState();
      const isAuth = await isUserAuthenticated();
      console.log('🔐 AUTH VALIDATION: User authenticated:', isAuth);
      return {
        isAuthenticated: isAuth,
        user: auth.currentUser,
        ready: authReady
      };
    } catch (error) {
      console.error('🔐 AUTH VALIDATION ERROR:', error);
      return {
        isAuthenticated: false,
        user: null,
        ready: false,
        error: error.message
      };
    }
  }

  // Force refresh auth token (useful before downloads)
  async function refreshAuthToken() {
    try {
      if (!currentUser) {
        throw new Error('No user logged in');
      }
      
      const token = await currentUser.getIdToken(true); // Force refresh
      console.log('🔐 AUTH TOKEN: Refreshed successfully');
      return token;
    } catch (error) {
      console.error('🔐 AUTH TOKEN ERROR:', error);
      throw new Error('Failed to refresh auth token: ' + error.message);
    }
  }

  useEffect(() => {
    let isMounted = true;
    
    const initializeAuth = async () => {
      try {
        setAuthError(null);
        console.log('🔐 AuthContext: Waiting for auth state...');
        
        // Wait for auth state to be ready from Firebase config
        await waitForAuthState();

        if (!isMounted) return;

        // Handle Google redirect result (if returning from Google sign-in)
        try {
          const redirectResult = await getRedirectResult(auth);
          if (redirectResult) {
            console.log('🔐 AuthContext: Google redirect sign-in successful');
            // User signed in via redirect - onAuthStateChanged will handle the rest
          }
        } catch (redirectError) {
          console.error('🔐 AuthContext: Google redirect error:', redirectError);
          if (isMounted) {
            setAuthError('Google sign-in failed: ' + redirectError.message);
          }
        }

        console.log('🔐 AuthContext: Auth state ready, setting up listener...');
        setAuthReady(true);
        
        // Now set up the auth state listener
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
          if (!isMounted) return;

          console.log('🔐 AuthContext: Auth state changed:', user ? `authenticated (${user.isAnonymous ? 'anonymous' : 'registered'})` : 'not authenticated');
          setCurrentUser(user);

          if (user) {
            // Check if this is an anonymous user
            if (user.isAnonymous) {
              console.log('👤 AuthContext: Anonymous user detected via Firebase Auth');

              // Load anonymous session data from localStorage
              const session = getAnonymousSession();
              if (session) {
                // Session exists - use it
                // Migration: Upgrade old sessions with < 2 credits to 2 credits
                // This ensures users with old sessions (0 or 1 credit) get upgraded to the new default (2 credits)
                let credits = session.credits || 0;
                if (credits < 2 && credits >= 0) {
                  console.log('⬆️ Upgrading anonymous session from', credits, 'to 2 credits');
                  credits = 2;
                  // Update the session in localStorage with new credit amount
                  updateAnonymousSession({ credits: 2 });
                }

                setAnonymousUser(session);
                setAnonymousCredits(credits);
                setIsAnonymous(true);
                console.log('👤 Anonymous session loaded:', credits, 'credits');
              } else {
                // Anonymous auth exists but no localStorage session
                // This can happen if localStorage was cleared or on page refresh
                // Create a minimal session object to maintain anonymous state
                console.warn('⚠️ Anonymous Firebase user but no localStorage session - creating minimal session');
                const minimalSession = {
                  isAnonymous: true,
                  uid: user.uid,
                  credits: 0,
                  createdAt: Date.now()
                };
                setAnonymousUser(minimalSession);
                setAnonymousCredits(0);
                setIsAnonymous(true); // KEEP as anonymous, don't flip to registered
                console.log('👤 Minimal anonymous session created with 0 credits');
              }
              setUserCredits(0);
            } else {
              // Regular authenticated user
              console.log('🔐 AuthContext: Registered user authenticated');

              // Clear anonymous session when registered user logs in
              clearAnonymousSession();
              setAnonymousUser(null);
              setAnonymousCredits(0);
              setIsAnonymous(false);

              // Fetch user credits immediately - optimistic defaults handle new users
              try {
                console.log('🔐 AuthContext: Fetching credits for user:', user.uid);
                await getUserCredits(user.uid, user);
                console.log('🔐 AuthContext: Credits fetch completed');
              } catch (error) {
                console.error('Error fetching user credits:', error);
                setAuthError('Failed to load user data');
              }
            }
          } else {
            // No authenticated user at all - should not happen with anonymous auth
            console.log('👤 AuthContext: No authenticated user');
            setUserCredits(0);
            setAnonymousUser(null);
            setAnonymousCredits(0);
            setIsAnonymous(false);
          }

          setLoading(false);
        }, (error) => {
          console.error('Auth state change error:', error);
          if (isMounted) {
            setAuthError('Authentication error occurred');
            setLoading(false);
          }
        });

        return () => {
          unsubscribe();
        };
        
      } catch (error) {
        console.error('🔐 AuthContext: Auth initialization error:', error);
        if (isMounted) {
          setAuthError('Failed to initialize authentication');
          setLoading(false);
        }
      }
    };

    const cleanup = initializeAuth();
    
    return () => {
      isMounted = false;
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(unsubscribe => {
          if (unsubscribe && typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      }
    };
  }, []);


  const value = {
    currentUser,
    userCredits,
    authReady,
    authError,
    loading,
    signup,
    login,
    signInWithGoogle,
    logout,
    resetPassword,
    createUserDocument,
    getUserCredits,
    updateUserCredits,
    updateUserProfile,
    validateAuthState,
    refreshAuthToken,
    // Anonymous session support
    anonymousUser,
    anonymousCredits,
    isAnonymous,
    loadAnonymousSession,
    updateAnonymousCredits,
    clearAnonymousSessionData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}